<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <!-- min-h rather than h: the caption under the product name carries the version, the
         house and who is signed in, and a fixed height forces them onto one line where
         the house becomes „Hotel T…“. Which house somebody is standing in decides which
         month is closed and which decision a PDF comes from, so it gets a second line
         rather than an ellipsis. -->
    <header class="sticky top-0 z-10 flex min-h-16 shrink-0 items-center gap-1 border-b border-default bg-default px-2 py-2 sm:px-4">
        <UIcon name="i-lucide-user-round-check" class="size-6 shrink-0 text-primary" />

        <div class="flex min-w-0 flex-1 flex-col leading-tight">
            <span class="truncate text-sm font-semibold text-highlighted">
                Imprion PMS <span class="font-normal text-muted">{{ $t('app.name') }}</span>
            </span>

            <span class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted">
                <AppVersion class="shrink-0" />

                <!-- The house, and a control only where the account actually reaches
                     more than one. A menu rather than a select: a native select sheet
                     covers a telephone's whole screen to change something that changes
                     once a season. -->
                <UDropdownMenu
                    v-if="appStore.properties.length > 1"
                    :items="propertyItems"
                    :content="{ align: 'start' }"
                    :ui="{ content: 'w-56' }"
                >
                    <UButton
                        :label="propertyName"
                        :aria-label="$t('profile.property')"
                        :ui="{ label: 'truncate' }"
                        trailing-icon="i-lucide-chevron-down"
                        color="neutral"
                        variant="link"
                        size="xs"
                        class="max-w-full shrink-0 gap-0.5 p-0 font-medium text-default"
                    />
                </UDropdownMenu>
                <span v-else-if="propertyName" class="max-w-full shrink-0 truncate font-medium text-default">
                    {{ propertyName }}
                </span>

                <!-- Whose portal this is. It matters here for a different reason than on
                     a shared trolley telephone: this is somebody's OWN handset, and the
                     one thing that must never be in doubt is that the arrival about to
                     be stamped is theirs and not the colleague who borrowed the phone. -->
                <span v-if="userName" class="flex max-w-full shrink-0 items-center gap-1">
                    <span class="text-dimmed">·</span>
                    <UIcon name="i-lucide-user" class="size-3 shrink-0" />
                    <span class="truncate">{{ userName }}</span>
                </span>
            </span>
        </div>

        <!-- Six languages, so a menu and not a two-way toggle. Icon-sized and showing the
             code it is on now, because „SL“ alone is not a word: the full name is on the
             menu row and on the label for a screen reader. -->
        <UDropdownMenu :items="languageItems" :content="{ align: 'end' }">
            <UButton
                :label="appStore.locale.toUpperCase()"
                :aria-label="`${$t('nav.language')}: ${languageName}`"
                :title="`${$t('nav.language')}: ${languageName}`"
                :ui="{ label: 'font-semibold' }"
                color="neutral"
                variant="ghost"
                size="xl"
            />
        </UDropdownMenu>

        <UButton
            :aria-label="`${$t('nav.theme')}: ${themeLabel}`"
            :title="`${$t('nav.theme')}: ${themeLabel}`"
            :icon="themeIcon"
            color="neutral"
            variant="ghost"
            size="xl"
            @click="cycleTheme"
        />

        <UDropdownMenu :items="accountItems" :content="{ align: 'end' }">
            <UButton
                :aria-label="$t('nav.account')"
                :title="$t('nav.account')"
                icon="i-lucide-circle-user-round"
                color="neutral"
                variant="ghost"
                size="xl"
            />
        </UDropdownMenu>

        <!-- Non-blocking progress hint: a corridor has bad signal often enough that a
             blocking spinner would be the screen most of the shift. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="absolute inset-x-0 bottom-0 h-0.5 animate-pulse bg-primary"
        />
    </header>
</template>

<script lang="ts" setup>
    import type { DropdownMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import AppVersion from '@/components/app/AppVersion.vue'
    import { AVAILABLE_LOCALES } from '@/libs/i18n'
    import { useAppStore } from '@/stores/app'

    const { t }    = useI18n()
    const router   = useRouter()
    const appStore = useAppStore()

    const userName = computed((): string => appStore.userData?.name ?? '')

    const propertyName = computed((): string => appStore.getProperty()?.name ?? '')

    const propertyItems = computed((): DropdownMenuItem[] => appStore.properties.map(property => ({
        label:    property.name,
        icon:     property.id === appStore.propertyId ? 'i-lucide-check' : undefined,
        onSelect: () => selectProperty(property.id),
    })))

    const selectProperty = (id: string) => {
        if (id === appStore.propertyId) return

        appStore.setPropertyId(id)
    }

    const languageName = computed((): string => t(`nav.language_${appStore.locale}`))

    const languageItems = computed((): DropdownMenuItem[] => AVAILABLE_LOCALES.map(code => ({
        label:    t(`nav.language_${code}`),
        icon:     code === appStore.locale ? 'i-lucide-check' : undefined,
        onSelect: () => appStore.changeLocale(code),
    })))

    // Three states, one button: Light → Dark → System → Light. Icon-only here, so the
    // current mode is named on the tooltip and for a screen reader.
    const themeOrder = ['light', 'dark', 'auto'] as const

    const themeIcon = computed((): string => {
        if (appStore.colorMode === 'light') return 'i-lucide-sun'
        if (appStore.colorMode === 'dark')  return 'i-lucide-moon'

        return 'i-lucide-monitor'
    })

    const themeLabel = computed((): string => {
        if (appStore.colorMode === 'light') return t('nav.theme_light')
        if (appStore.colorMode === 'dark')  return t('nav.theme_dark')

        return t('nav.theme_system')
    })

    const cycleTheme = () => {
        const current = themeOrder.indexOf(appStore.colorMode as typeof themeOrder[number])

        appStore.colorMode = themeOrder[(current + 1) % themeOrder.length]
    }

    /**
     * The profile and the way out, behind one icon.
     *
     * „Moj profil“ is not a sixth tab in the bar because it is opened twice a year and a
     * sixth thumb-width tab would cost the other five their labels. Signing out sits
     * beside it rather than being a button of its own for the reason a shared handset
     * makes urgent: a one-tap sign-out next to the language button is a sign-out somebody
     * presses by accident in a corridor, and this portal has no other way back in than
     * typing a password.
     */
    const accountItems = computed((): DropdownMenuItem[][] => [
        [{
            label:    t('nav.profile'),
            icon:     'i-lucide-user-round-cog',
            onSelect: () => { void router.push({ name: 'profile' }) },
        }],
        [{
            label:    t('nav.logout'),
            icon:     'i-lucide-log-out',
            color:    'error' as const,
            onSelect: () => logout(),
        }],
    ])

    const logout = () => {
        appStore.setLogout()
        void router.push({ name: 'login' })
    }
</script>
